/**
 * ErrorBoundary.tsx
 *
 * Graceful error handling for Write Library.
 * Catches React render errors and shows a recovery UI
 * instead of crashing the entire app.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <ManuscriptEditor />
 *   </ErrorBoundary>
 *
 *   // With custom fallback:
 *   <ErrorBoundary fallback={<p>Something broke.</p>}>
 *     <ConstellationView />
 *   </ErrorBoundary>
 *
 *   // With section name for better error messages:
 *   <ErrorBoundary section="Constellation">
 *     <ConstellationView />
 *   </ErrorBoundary>
 */

import React from 'react';

interface ErrorBoundaryProps {
    children: React.ReactNode;
    /** Custom fallback UI. If omitted, renders default recovery card. */
    fallback?: React.ReactNode;
    /** Human-readable name of the section (for error messages) */
    section?: string;
    /** Called when an error is caught — use for Sentry/logging */
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error(`[ErrorBoundary] Caught in "${this.props.section ?? 'unknown'}":`, error, errorInfo);
        this.props.onError?.(error, errorInfo);
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null });
    };

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        if (this.props.fallback) {
            return this.props.fallback;
        }

        const { section } = this.props;
        const { error } = this.state;

        return (
            <DefaultErrorFallback
                section={section}
                error={error}
                onReset={this.handleReset}
            />
        );
    }
}

// ─── Default fallback UI ──────────────────────────────────────────────────────

interface DefaultErrorFallbackProps {
    section?: string;
    error: Error | null;
    onReset: () => void;
}

function DefaultErrorFallback({ section, error, onReset }: DefaultErrorFallbackProps) {
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '48px 24px',
                textAlign: 'center',
                minHeight: '200px',
                color: 'var(--studio-text, #2C2416)',
                background: 'var(--studio-paper, #FAF7F0)',
                borderRadius: '12px',
                border: '1px solid var(--studio-border, rgba(44,36,22,0.12))',
            }}
        >
            {/* Quill icon */}
            <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ opacity: 0.4, marginBottom: '16px' }}
            >
                <path d="M20 6C20 6 18 3 12 3C6 3 4 8 4 12C4 16 6 18 6 18" />
                <path d="M6 18C6 18 8 14 12 14C16 14 18 16 18 21" />
                <path d="M12 14L8 21" />
            </svg>

            <h3
                style={{
                    fontFamily: 'var(--studio-font, "Crimson Text", Georgia, serif)',
                    fontSize: '20px',
                    fontWeight: 600,
                    marginBottom: '8px',
                }}
            >
                {section ? `${section} ran into trouble` : 'Something went wrong'}
            </h3>

            <p
                style={{
                    fontSize: '14px',
                    opacity: 0.6,
                    maxWidth: '320px',
                    lineHeight: 1.6,
                    marginBottom: '24px',
                    fontFamily: 'sans-serif',
                }}
            >
                Your writing is safe. This part of the app hit an unexpected snag.
                {error?.message && (
                    <span style={{ display: 'block', marginTop: '8px', fontSize: '12px', fontFamily: 'monospace', opacity: 0.5 }}>
                        {error.message}
                    </span>
                )}
            </p>

            <button
                onClick={onReset}
                style={{
                    padding: '10px 24px',
                    borderRadius: '8px',
                    border: '1px solid var(--studio-border, rgba(44,36,22,0.2))',
                    background: 'var(--studio-accent, #8B7355)',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontFamily: 'sans-serif',
                    fontWeight: 500,
                }}
            >
                Try again
            </button>
        </div>
    );
}

// ─── Convenience wrappers for common sections ─────────────────────────────────

export const EditorBoundary = ({ children }: { children: React.ReactNode }) => (
    <ErrorBoundary section="Manuscript Editor">{children}</ErrorBoundary>
);

export const ConstellationBoundary = ({ children }: { children: React.ReactNode }) => (
    <ErrorBoundary section="Constellation">{children}</ErrorBoundary>
);

export const TimelineBoundary = ({ children }: { children: React.ReactNode }) => (
    <ErrorBoundary section="Chronoscope">{children}</ErrorBoundary>
);

export const CompostBoundary = ({ children }: { children: React.ReactNode }) => (
    <ErrorBoundary section="Compost Heap">{children}</ErrorBoundary>
);

export const SidebarBoundary = ({ children }: { children: React.ReactNode }) => (
    <ErrorBoundary section="Sidebar">{children}</ErrorBoundary>
);
